import React, { useRef, useEffect, useMemo } from 'react';
import tldjs from 'tldjs';
import { Box, FormError, FormTextarea, Loader } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Field, Form, Formik } from 'formik';
import { Flex, Link, Box as RebassBox, Text } from 'rebass/styled-components';
import { useSelector } from 'react-redux';
import { urlWithoutProtocolRegEx } from 'helpers/regExs';
import helperGet from '../../helpers/get';

import { HubspotEmbed } from '../../components/molecules/hubspotEmbed';
import strings from '../../strings';
import { trackEvent } from '../../helpers/analytics';
import { useBulkAddCompanyByDomainMutation, useGetSubscriptionsQuery } from '../../services/organizationsV2';
import { activeNetworkSelector } from '../../redux/selectors';
import { hasErrorSelector, needsVerificationSelector } from './redux/selectors';
import CompaniesAdded from './companiesAdded';

const initialValues = {
  websites: [],
};

const getInvalidDomains = (websites) =>
  websites
    .filter((website) => website && website.length)
    .filter((website) => !RegExp(urlWithoutProtocolRegEx).test(website));

const AddBulkCompanyWithDomain = ({ goToCompaniesList, showAddCompanyWithoutDomain }) => {
  const formRef = useRef();
  const activeNetwork = useSelector(activeNetworkSelector);
  const [bulkAddCompanyByDomain, { isLoading: isSubmitting, data: bulkAddCompanyByDomainData }] =
    useBulkAddCompanyByDomainMutation();
  const { data: subscriptions, isLoading } = useGetSubscriptionsQuery(activeNetwork.id);
  const needsVerification = useSelector(needsVerificationSelector);
  const hasError = useSelector(hasErrorSelector);
  const canAddCompanies = useMemo(() => {
    const sub = subscriptions?.activeSubscriptions?.find((subscription) =>
      subscription.subscriptionItems.find((item) => item?.stairstep?.unit === 'companies'),
    ); // eslint-disable-line function-paren-newline

    if (sub) {
      const activeStairstep = sub.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies');

      if (activeStairstep) {
        return activeStairstep?.stairstep?.used < activeStairstep?.stairstep?.total;
      }
    }

    return true;
  }, [subscriptions]);

  useEffect(() => {
    if (hasError && !needsVerification && formRef.current) {
      formRef.current.setStatus({ error: strings.genericError });
    }
  }, [hasError, needsVerification, formRef]);

  const handleSubmit = async ({ websites }) => {
    const domains = await websites.map((website) => tldjs.getDomain(website));
    bulkAddCompanyByDomain({ networkId: activeNetwork.id, domains });
  };

  if (bulkAddCompanyByDomainData?.meta) {
    return <CompaniesAdded onDone={goToCompaniesList} meta={bulkAddCompanyByDomainData.meta} />;
  }

  const onFormValidation = (values) => {
    const { websites } = values;

    if (!websites || !websites.filter((website) => website.trim().length > 0).length) {
      return { websites: 'Required' };
    }

    const invalidDomains = getInvalidDomains(values.websites);
    if (invalidDomains.length) {
      return { invalidDomains };
    }

    const uniqueWebsites = new Set(websites);

    if (uniqueWebsites.size !== websites.length) {
      return { websites: 'There are duplicates in your list. Please remove them to proceed.' };
    }

    return {};
  };

  if (isLoading) {
    return (
      <Flex mt="80px">
        <Loader />
      </Flex>
    );
  }

  if (!canAddCompanies) {
    trackEvent('upsell:paywall_shown', { origin: 'add_companies' });

    return (
      <Flex mt="80px" flexDirection="column">
        <RebassBox as="header">
          <Text fontSize="24px" fontWeight="semibold" color="text.dark">
            Upgrade to add more companies to your job board
          </Text>
          <Text mt="8px" fontSize="18px">
            Schedule a quick call with our team to explore your options and ensure every company in your network gets
            the visibility it deserves.
          </Text>
          <Text mt="12px" fontSize="18px">
            If you prefer, email us at{' '}
            <Link
              href="mailto:success@getro.com"
              sx={{
                color: 'text.main',
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              success@getro.com
            </Link>
            .
          </Text>
        </RebassBox>
        <RebassBox mt="16px" height="680px" width="750px">
          <HubspotEmbed type="companies" />
        </RebassBox>
      </Flex>
    );
  }

  return (
    <Flex mt="80px">
      <Formik initialValues={initialValues} onSubmit={handleSubmit} innerRef={formRef} validate={onFormValidation}>
        {({ status, setFieldValue, values, errors }) => (
          <Form>
            <RebassBox
              sx={{
                '.Box__title': {
                  fontWeight: 600,
                },
                '.Box__footer-back-button': {
                  fontWeight: 400,
                },
              }}
            >
              <Box
                className="AddCompany"
                errorText={helperGet(status, 'error', null)}
                onCancelClick={goToCompaniesList}
                loading={isSubmitting}
                editing
                saveAsSubmit
                title="New companies websites"
                saveText="Add companies"
                onBackClick={showAddCompanyWithoutDomain}
                backText="Add company without website"
              >
                <Field
                  type="text"
                  name="websites"
                  component={FormTextarea}
                  value={values.websites?.join(',').replace(/,/g, '\n')}
                  onChange={(e) => {
                    setFieldValue('websites', e.target.value.split(/[\n,]+/));
                  }}
                  height="120px"
                  placeholder={`website1.com\nwebsite2.com\nwebsite3.com\n...`}
                  label="Companies websites"
                  helpText="Add the websites of the companies you want to add, one per line. You can copy and paste them directly from a spreadsheet."
                />
                {errors.invalidDomains && (
                  <RebassBox sx={{ padding: 10, marginTop: 2 }} backgroundColor="red.20">
                    <FormError>Review the format of the following websites:</FormError>
                    <RebassBox as="ul" margin={0}>
                      {errors.invalidDomains.map((website) => (
                        <Text fontSize="14px" as="li" color="red.600">
                          {website}
                        </Text>
                      ))}
                    </RebassBox>
                  </RebassBox>
                )}
              </Box>
            </RebassBox>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};

AddBulkCompanyWithDomain.propTypes = {
  goToCompaniesList: PropTypes.func.isRequired,
  showAddCompanyWithoutDomain: PropTypes.func,
};

AddBulkCompanyWithDomain.defaultProps = {
  showAddCompanyWithoutDomain: null,
};

export default AddBulkCompanyWithDomain;
